interface SearchItem {
    [key: string]: any;
  }
  
  /**
   * Fuzzy search function with weight-based scoring and penalties for non-matching characters
   * @param {SearchItem[]} items - Array of objects to search
   * @param {string} property - The object property to search in
   * @param {string} value - The search value
   * @returns {SearchItem[]} - Array of matching objects, sorted by best match
   */
function fuzzySearch(items: SearchItem[], property: string, value: string): SearchItem[] {
    
    const normalizeString = (str: string) => str.toLowerCase().replace(/\s+/g, '');
  
    /**
     * Calculate fuzzy match score with penalties
     * @param {string} target - Target string to search within
     * @param {string} query - Query string to search for
     * @returns {number} - Score based on match quality and penalties
     */
    const calculateFuzzyScore = (target: string, query: string): number => {
      
        target = normalizeString(target);
      query = normalizeString(query);
  
      let queryIndex = 0;
      let score = 0;
      let consecutiveBonus = 0;
      let gapPenalty = 0;
  
      for (let i = 0; i < target.length; i++) {
        if (target[i] === query[queryIndex]) {
          // Increase score for each matching character
          score += 1;
  
          // Add a bonus for consecutive matches
          consecutiveBonus += 1;
          score += consecutiveBonus;
  
          // Move to the next query character
          queryIndex++;
        } else {
          // Penalize gaps (non-consecutive matching characters)
          consecutiveBonus = 0;
          gapPenalty += 1;
        }
  
        if (queryIndex === query.length) {
          break;
        }
      }
  
      // Add a length bonus for full query matches
      if (queryIndex === query.length) {
        score += query.length;
      }
  
      // Penalize for gaps and unmatched characters in the target string
      const unmatchedCharacters = target.length - queryIndex;
      const penalty = gapPenalty + unmatchedCharacters;
  
      // Deduct penalties from the final score
      score -= penalty;
  
      return score;
    };
  
    // Return objects sorted by the best fuzzy match score
    return items
      .map(item => ({
        item,
        score: calculateFuzzyScore(item[property]?.toString() || '', value),
      }))
      .filter(result => result.score > 0) // Only keep results with a score greater than 0
      .sort((a, b) => b.score - a.score) // Sort by highest score
      .map(result => result.item); // Return the sorted objects
  }
  
export default fuzzySearch;

  // Example usage
//   const data = [
//     { name: 'Apple', category: 'Fruit' },
//     { name: 'Orange', category: 'Fruit' },
//     { name: 'Banana', category: 'Fruit' },
//     { name: 'Grapes', category: 'Fruit' },
//     { name: 'Pineapple', category: 'Fruit' },
//   ];
  
//   console.log(fuzzySearch(data, 'name', 'appl')); 
  